import clsx from 'clsx';
import React, { useCallback, useRef, useState } from 'react';
import { loadWhop } from '@whop/elements';
import { KycElement, Verifications, WhopElements } from '@whop/elements-react';

import { SecondaryButtonV2 } from '@fragment/ui/src/components/Button/SecondaryButtonV2/SecondaryButtonV2';
import { LoadingText } from '@fragment/ui/src/components/LoadingText';
import { POSTHOG_MASK_CLASS } from '@fragment/ui/src/constants';

import type { CreatePaymentAccountVerificationTokenMutation } from '../../../../hooks/internalApi';

export type PaymentAccountVerificationSession = Extract<
  CreatePaymentAccountVerificationTokenMutation['createPaymentAccountVerificationToken'],
  { __typename: 'CreatePaymentAccountVerificationTokenResult' }
>;

type Props = {
  session: PaymentAccountVerificationSession;
  createVerificationToken: () => Promise<PaymentAccountVerificationSession>;
  onCompleted: VoidFunction;
};

export const PaymentAccountVerification = ({
  session,
  createVerificationToken,
  onCompleted,
}: Props) => {
  const [elements] = useState(() => loadWhop());
  const [hasError, setHasError] = useState(false);
  const [retryLoad, setRetryLoad] = useState<VoidFunction | undefined>(
    undefined
  );
  const [attempt, setAttempt] = useState(0);
  const initialSession = useRef<PaymentAccountVerificationSession | undefined>(
    session
  );
  const getToken = useCallback(async () => {
    const initialTokenSession = initialSession.current;
    initialSession.current = undefined;
    if (
      initialTokenSession &&
      Date.parse(initialTokenSession.expiresAt) > Date.now()
    )
      return initialTokenSession.accessToken;
    try {
      return (await createVerificationToken()).accessToken;
    } catch (error) {
      setHasError(true);
      throw error;
    }
  }, [createVerificationToken]);

  return (
    <>
      {hasError && (
        <div role="alert" className="flex flex-col gap-f2">
          <span>Unable to load verification. Please try again.</span>
          <SecondaryButtonV2
            onClick={() => {
              setHasError(false);
              if (retryLoad) {
                setRetryLoad(undefined);
                retryLoad();
              } else setAttempt((value) => value + 1);
            }}
          >
            Retry
          </SecondaryButtonV2>
        </div>
      )}
      <div className={clsx(POSTHOG_MASK_CLASS, hasError && 'hidden')}>
        <WhopElements
          elements={elements}
          environment={session.mode}
          onLoadError={(_error, retry) => {
            setHasError(true);
            setRetryLoad(() => retry);
          }}
        >
          <Verifications
            key={attempt}
            kind="business"
            accountId={session.accountId}
            getToken={getToken}
          >
            <KycElement
              onCompleted={onCompleted}
              onLoadFailed={() => setHasError(true)}
              fallback={<LoadingText text="Loading verification" />}
            />
          </Verifications>
        </WhopElements>
      </div>
    </>
  );
};
